/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Tooltip } from "react-tippy";
import { v4 as uuidv4 } from "uuid";
import ProductDetails from "../ProductDetails/ProductDetails.component";
import AddDelControl from "../AddDelControl/AddDelControl.component";

import PenIcon from "../../../Common/components/Icons/Pen/PenIcon";
import BinIcon from "../../../Common/components/Icons/BinIcon/BinIcon";
import InfoIcon from "../../../Common/components/Icons/Info/InfoIcon";
import AddIcon from "../../../Common/components/Icons/Add/AddIcon";
import LessIcon from "../../../Common/components/Icons/LessIcon/LessIcon";

import {
  selectCalculatedElectricalInstallation,
  selectIsTrackClosed,
  selectNbSegments,
  setCalculatedElectricalInstallation
} from "../../store/slices/mc12";
import t from "../../../Common/utils/translation/translator";
import { getProductImgUrl } from "../../../Common/utils/api/api";
import { calculateNbPsInPowerGroups, getMaxPowerSupplies, getMinPowerSupplies } from "../../utils/func.utils";
import { showSnackbar } from "../../../Retrofit/store/slices/retrofit";

const PowerGroup = ({ index, id }) => {
  // console.log("PowerGroup", id);
  const dispatch = useDispatch();
  const isTrackClosed = useSelector(selectIsTrackClosed);
  const nbSegments = useSelector(selectNbSegments);
  const calculatedElectricalInstallation = useSelector(selectCalculatedElectricalInstallation);
  const selectedPowerGroup = calculatedElectricalInstallation.powerGroups.find((pg) => pg.id === id);
  const [showMaxPSMsg, setShowMaxPSMsg] = useState(false);
  const [showMinPSMsg, setShowMinPSMsg] = useState(false);
  const [showEditNameInput, setShowEditNameInput] = useState(false);
  const name = React.createRef();
  const title = selectedPowerGroup.name
    ? selectedPowerGroup.name
    : `${t("mc12.ui.step.electrical.title.powergroup")} ${selectedPowerGroup.id}`;

  const deletePowerGroup = () => {
    // console.log("deletePowerGroup");
    if (
      calculatedElectricalInstallation.powerGroups.length === 1 ||
      calculateNbPsInPowerGroups(calculatedElectricalInstallation.powerGroups) <= getMinPowerSupplies(nbSegments)
    ) {
      if (calculateNbPsInPowerGroups(calculatedElectricalInstallation.powerGroups) <= getMinPowerSupplies(nbSegments)) {
        dispatch(showSnackbar({ id: "MC12_POWERSUPPLY_MINIMUM" }));
      }
      return;
    }

    const tmpCalculatedElectricalInstallation = JSON.parse(JSON.stringify(calculatedElectricalInstallation));
    const selectedPowerGroupIndex = tmpCalculatedElectricalInstallation.powerGroups.findIndex(
      (pg) => pg.id === selectedPowerGroup.id
    );
    if (selectedPowerGroupIndex !== -1) {
      tmpCalculatedElectricalInstallation.powerGroups.splice(selectedPowerGroupIndex, 1);
      // Recalculate all
      if (tmpCalculatedElectricalInstallation.powerGroups.length === 1) {
        // console.log("Recalculate all for the last remaining power group");
        const nbPs = getMinPowerSupplies(nbSegments);
        dispatch(
          setCalculatedElectricalInstallation(
            recalculateElectricalInstallation(
              tmpCalculatedElectricalInstallation,
              tmpCalculatedElectricalInstallation.powerGroups[0].id,
              calculateNbPsInPowerGroups(tmpCalculatedElectricalInstallation.powerGroups) >= nbPs
                ? calculateNbPsInPowerGroups(tmpCalculatedElectricalInstallation.powerGroups)
                : nbPs
            )
          )
        );
      } else {
        dispatch(
          setCalculatedElectricalInstallation(
            recalculateElectricalInstallation(
              tmpCalculatedElectricalInstallation,
              tmpCalculatedElectricalInstallation.powerGroups[
                tmpCalculatedElectricalInstallation.powerGroups.length - 1
              ].id,
              tmpCalculatedElectricalInstallation.powerGroups[
                tmpCalculatedElectricalInstallation.powerGroups.length - 1
              ].powerSupply.quantity
            )
          )
        );
      }
    }
  };

  const deletePowerSupply = () => {
    // console.log("deletePowerSupply");
    const tmpCalculatedElectricalInstallation = JSON.parse(JSON.stringify(calculatedElectricalInstallation));
    const selectedPowerGroupIndex = tmpCalculatedElectricalInstallation.powerGroups.findIndex(
      (pg) => pg.id === selectedPowerGroup.id
    );
    if (selectedPowerGroupIndex !== -1) {
      const nbPsInPowerGroups = calculateNbPsInPowerGroups(tmpCalculatedElectricalInstallation.powerGroups);
      if (nbPsInPowerGroups <= getMinPowerSupplies(nbSegments)) {
        dispatch(showSnackbar({ id: "MC12_POWERSUPPLY_MINIMUM" }));
        return;
      }
      if (tmpCalculatedElectricalInstallation.powerGroups[selectedPowerGroupIndex].powerSupply.quantity > 1) {
        const nbPsInPowerGroups = calculateNbPsInPowerGroups(tmpCalculatedElectricalInstallation.powerGroups);
        if (nbPsInPowerGroups > getMinPowerSupplies(nbSegments)) {
          const nbPs = --tmpCalculatedElectricalInstallation.powerGroups[selectedPowerGroupIndex].powerSupply.quantity;
          dispatch(
            setCalculatedElectricalInstallation(
              recalculateElectricalInstallation(tmpCalculatedElectricalInstallation, id, nbPs)
            )
          );
        } else {
          dispatch(showSnackbar({ id: "MC12_POWERSUPPLY_MINIMUM" }));
        }
      } else {
        setShowMinPSMsg(true);
      }
    }
    setShowMaxPSMsg(false);
  };

  const addPowerSupply = () => {
    // console.log("addPowerSupply");
    const tmpCalculatedElectricalInstallation = JSON.parse(JSON.stringify(calculatedElectricalInstallation));
    const selectedPowerGroupIndex = tmpCalculatedElectricalInstallation.powerGroups.findIndex(
      (pg) => pg.id === selectedPowerGroup.id
    );
    if (selectedPowerGroupIndex !== -1) {
      const nbPsInPowerGroups = calculateNbPsInPowerGroups(tmpCalculatedElectricalInstallation.powerGroups);
      if (nbPsInPowerGroups >= getMaxPowerSupplies(nbSegments)) {
        dispatch(showSnackbar({ id: "MC12_POWERSUPPLY_MAXIMUM" }));
        return;
      }
      if (tmpCalculatedElectricalInstallation.powerGroups[selectedPowerGroupIndex].powerSupply.quantity < 3) {
        const nbPs = ++tmpCalculatedElectricalInstallation.powerGroups[selectedPowerGroupIndex].powerSupply.quantity;
        dispatch(
          setCalculatedElectricalInstallation(
            recalculateElectricalInstallation(tmpCalculatedElectricalInstallation, id, nbPs)
          )
        );
      } else {
        setShowMaxPSMsg(true);
      }
    }
    setShowMinPSMsg(false);
  };

  const setCableLength = (value) => {
    // console.log("setCableLength", value);
    const tmpCalculatedElectricalInstallation = JSON.parse(JSON.stringify(calculatedElectricalInstallation));
    const selectedPowerGroupIndex = tmpCalculatedElectricalInstallation.powerGroups.findIndex(
      (pg) => pg.id === selectedPowerGroup.id
    );
    if (selectedPowerGroupIndex !== -1) {
      const selectedPowerCableIndex = tmpCalculatedElectricalInstallation.powerGroups[
        selectedPowerGroupIndex
      ].powerCables.findIndex((cable) => cable.length === parseInt(value, 10));
      if (selectedPowerCableIndex !== -1) {
        const quantity = tmpCalculatedElectricalInstallation.powerGroups[selectedPowerGroupIndex].powerCables.reduce(
          (acc, cable) => acc + cable.quantity,
          0
        );
        tmpCalculatedElectricalInstallation.powerGroups[selectedPowerGroupIndex].powerCables.map(
          (powerCable) => (powerCable.quantity = 0)
        );
        tmpCalculatedElectricalInstallation.powerGroups[selectedPowerGroupIndex].powerCables[
          selectedPowerCableIndex
        ].quantity = quantity;
        dispatch(setCalculatedElectricalInstallation(tmpCalculatedElectricalInstallation));
      }
    }
  };

  const addBreakingResistor = () => {
    // console.log("addBreakingResistor");
    const tmpCalculatedElectricalInstallation = JSON.parse(JSON.stringify(calculatedElectricalInstallation));
    const selectedPowerGroupIndex = tmpCalculatedElectricalInstallation.powerGroups.findIndex(
      (pg) => pg.id === selectedPowerGroup.id
    );
    if (selectedPowerGroupIndex !== -1) {
      tmpCalculatedElectricalInstallation.powerGroups[selectedPowerGroupIndex].extBrakingResistor.quantity =
        tmpCalculatedElectricalInstallation.powerGroups[selectedPowerGroupIndex].connectionModule.quantity;
      dispatch(setCalculatedElectricalInstallation(tmpCalculatedElectricalInstallation));
    }
  };

  const removeBreakingResistor = () => {
    // console.log("removeBreakingResistor");
    const tmpCalculatedElectricalInstallation = JSON.parse(JSON.stringify(calculatedElectricalInstallation));
    const selectedPowerGroupIndex = tmpCalculatedElectricalInstallation.powerGroups.findIndex(
      (pg) => pg.id === selectedPowerGroup.id
    );
    if (selectedPowerGroupIndex !== -1) {
      tmpCalculatedElectricalInstallation.powerGroups[selectedPowerGroupIndex].extBrakingResistor.quantity = 0;
      dispatch(setCalculatedElectricalInstallation(tmpCalculatedElectricalInstallation));
    }
  };

  const changePowerGroupName = (newName) => {
    // console.log("changePowerGroupName", id, newName);
    const tmpCalculatedElectricalInstallation = JSON.parse(JSON.stringify(calculatedElectricalInstallation));
    const selectedPowerGroupIndex = tmpCalculatedElectricalInstallation.powerGroups.findIndex(
      (pg) => pg.id === selectedPowerGroup.id
    );
    if (selectedPowerGroupIndex !== -1) {
      if (newName !== "") {
        tmpCalculatedElectricalInstallation.powerGroups[selectedPowerGroupIndex].name = newName;
        dispatch(setCalculatedElectricalInstallation(tmpCalculatedElectricalInstallation));
      }
    }
  };

  function recalculateElectricalInstallation(electricalInstallation, powerGroupId, powerSupplyNumber) {
    // console.log("recalculateElectricalInstallation");
    const tmpElectricalInstallation = JSON.parse(JSON.stringify(electricalInstallation));
    const selectedPowerGroupIndex = tmpElectricalInstallation.powerGroups.findIndex((pg) => pg.id === powerGroupId);
    // Number of powerSupply
    tmpElectricalInstallation.powerGroups[selectedPowerGroupIndex].powerSupply.quantity = powerSupplyNumber;
    // Number of connectionModule
    tmpElectricalInstallation.powerGroups[selectedPowerGroupIndex].connectionModule.quantity = powerSupplyNumber;
    // Number of power disconnectors between segments
    if (isTrackClosed) {
      tmpElectricalInstallation.powerDisconnector.quantity =
        tmpElectricalInstallation.powerGroups.length === 1 ? 0 : tmpElectricalInstallation.powerGroups.length;
    } else {
      tmpElectricalInstallation.powerDisconnector.quantity = tmpElectricalInstallation.powerGroups.length - 1;
    }
    tmpElectricalInstallation.powerGroups[selectedPowerGroupIndex].powerInterPlug.quantity = powerSupplyNumber;
    // Number of "Power Interconnects"
    const numberOfPowerInterconnects = tmpElectricalInstallation.powerGroups.reduce(
      (acc, curr) => acc + curr.powerInterPlug.quantity,
      0
    );
    let powerInterconnectsQuantity = 0;
    if (isTrackClosed) {
      powerInterconnectsQuantity =
        nbSegments - numberOfPowerInterconnects - tmpElectricalInstallation.powerDisconnector.quantity;
    } else {
      powerInterconnectsQuantity =
        nbSegments - numberOfPowerInterconnects - tmpElectricalInstallation.powerDisconnector.quantity + 1;
    }
    // const piece_10_quantity = Math.floor(powerInterconnectsQuantity / 10);
    // const piece_1_quantity = Math.ceil(powerInterconnectsQuantity % 10);
    const piece_1_quantity = powerInterconnectsQuantity;
    const piece_1_index = tmpElectricalInstallation.powerInterSeg.findIndex((p) => p.quantityPerSet === "1_PIECE");
    // const piece_10_index = tmpElectricalInstallation.powerInterSeg.findIndex((p) => p.quantityPerSet === "10_PIECES");
    tmpElectricalInstallation.powerInterSeg[piece_1_index].quantity = piece_1_quantity;
    // tmpElectricalInstallation.powerInterSeg[piece_10_index].quantity = piece_10_quantity;
    // Number of cables
    const selectedPowerCableIndex = tmpElectricalInstallation.powerGroups[
      selectedPowerGroupIndex
    ].powerCables.findIndex((c) => c.quantity !== 0);
    tmpElectricalInstallation.powerGroups[selectedPowerGroupIndex].powerCables[selectedPowerCableIndex].quantity =
      powerSupplyNumber;
    if (tmpElectricalInstallation.powerGroups[selectedPowerGroupIndex].extBrakingResistor.quantity > 0) {
      tmpElectricalInstallation.powerGroups[selectedPowerGroupIndex].extBrakingResistor.quantity = powerSupplyNumber;
    }
    return tmpElectricalInstallation;
  }
  return (
    <>
      <div className="flex items-center">
        <div className="bg-gray-200 w-full h-[1px]"> </div>
        {index > 0 && (
          <div className="flex w-full justify-center items-center gap-x-3">
            <img
              className="w-16"
              src={getProductImgUrl(calculatedElectricalInstallation.powerDisconnector.commercialReference)}
              alt=""
            />
            <span className="text-lg">{index === 1 && isTrackClosed ? 2 : 1}</span>
            <span className="text-sm">{t("mc12.ui.step.electrical.title.power_disconnector")}</span>
          </div>
        )}
        <div className="bg-gray-200 w-full h-[1px]"> </div>
      </div>
      <div className="border-[1px] border-[#E7E6E6] bg-gray-50 p-4 rounded md:flex md:justify-between">
        <div className="flex flex-col">
          <div className="flex flex-col md:grow md:flex-row gap-3 items-center md:mt-[-20px]">
            {!showEditNameInput && <span className="font-medium text-primary ">{title}</span>}
            {showEditNameInput && <input type="text" ref={name} placeholder={title} />}
            {!showEditNameInput && (
              <button
                onClick={() => {
                  setShowEditNameInput(true);
                }}
                type="button"
                className="flex flex-col items-center justify-center "
              >
                <PenIcon color="green" size={15} />
              </button>
            )}
            {showEditNameInput && (
              <button
                onClick={() => {
                  setShowEditNameInput(false);
                  changePowerGroupName(name.current.value);
                }}
                type="button"
                className="flex flex-col items-center justify-center "
              >
                <AddIcon color="green" size={15} />
              </button>
            )}
            {!showEditNameInput && (
              <button
                onClick={() => deletePowerGroup()}
                type="button"
                className="flex flex-col items-center justify-center"
              >
                <BinIcon
                  color={
                    calculatedElectricalInstallation.powerGroups.length === 1 ||
                    calculateNbPsInPowerGroups(calculatedElectricalInstallation.powerGroups) <=
                      getMinPowerSupplies(nbSegments)
                      ? "grey"
                      : "red"
                  }
                  size={15}
                />
              </button>
            )}
          </div>
          <div className="flex flex-col md:grow md:flex-row">
            {Array.from({ length: selectedPowerGroup.powerSupply.quantity }).map((_, index) => (
              <div key={uuidv4()} className="w-32 md:self-end mx-[-12px]">
                <img src={getProductImgUrl(selectedPowerGroup.powerSupply.commercialReference)} alt="" />
              </div>
            ))}
            <div className="md:self-end md:mb-3 mx-4">
              <AddDelControl
                title={t("mc12.ui.step.electrical.title.powersuply")}
                onDel={deletePowerSupply}
                onAdd={addPowerSupply}
                onDelDisabled={
                  selectedPowerGroup.powerSupply.quantity === 1 ||
                  calculateNbPsInPowerGroups(calculatedElectricalInstallation.powerGroups) <=
                    getMinPowerSupplies(nbSegments)
                }
                onAddDisabled={
                  selectedPowerGroup.powerSupply.quantity === 3 ||
                  calculateNbPsInPowerGroups(calculatedElectricalInstallation.powerGroups) >=
                    getMaxPowerSupplies(nbSegments)
                }
              />
            </div>
          </div>
          {showMaxPSMsg && <div className="text-red-700">{t("mc12.ui.step.electrical.title.powersuply.maximum")}</div>}
          {showMinPSMsg && <div className="text-red-700">{t("mc12.ui.step.electrical.title.powersuply.minimum")}</div>}
        </div>
        <div className="md:flex gap-3">
          <div className="md:flex px-4 py-2 justify-between items-center bg-white rounded shadow-[0_0_4px_0_rgba(98,100,105,0.20)] gap-x-5">
            <ProductDetails
              num={selectedPowerGroup.connectionModule.quantity}
              title={t("mc12.ui.step.electrical.title.connection_module")}
              image={getProductImgUrl(selectedPowerGroup.connectionModule.commercialReference)}
            />
            <div className="h-[85%] w-px bg-[#E7E6E6]" />
            <ProductDetails
              num={selectedPowerGroup.powerCables.reduce((a, b) => a + b.quantity, 0)}
              title={t("mc12.ui.step.electrical.title.power_cable")}
              image={getProductImgUrl(selectedPowerGroup.powerCables[0].commercialReference)}
              options={selectedPowerGroup.powerCables.map((cable) => {
                return { value: cable.length + " m", selected: cable.quantity > 0 ? true : false };
              })}
              onChange={(e) => setCableLength(e.target.value)}
            />
            <div className="h-[85%] w-px bg-[#E7E6E6]" />
            <ProductDetails
              num={selectedPowerGroup.powerInterPlug.quantity}
              title={t("mc12.ui.step.electrical.title.power_interconnect")}
              image={getProductImgUrl(selectedPowerGroup.powerInterPlug.commercialReference)}
            />
          </div>
          <div className="flex flex-col justify-between bg-white shadow-[0_0_4px_0_rgba(0,0,0,0.20)] max-w-min">
            <div
              className={`flex flex-col  border-t-2 border-x-2 border-[#299BCD] ${
                !selectedPowerGroup.extBrakingResistor.quantity > 0 ? "border-dashed" : "border-solid"
              } rounded-t px-3 pb-2`}
            >
              <div className="has-tooltip-info flex justify-end">
                <Tooltip
                  title={t("mc12.ui.step.electrical.title.braking_tooltip")}
                  duration={200}
                  arrow
                  size="small"
                  theme="light"
                >
                  <InfoIcon color="#42B4E6" size={13} />
                </Tooltip>
              </div>
              <img
                src={getProductImgUrl(selectedPowerGroup.extBrakingResistor.commercialReference)}
                alt=""
                className={`self-center ${!selectedPowerGroup.extBrakingResistor.quantity > 0 && "opacity-40"}`}
              />
              <div className="flex items-center gap-x-2">
                {selectedPowerGroup.extBrakingResistor.quantity > 0 && (
                  <span className={`text-lg ${!selectedPowerGroup.extBrakingResistor.quantity > 0 && "opacity-0"}`}>
                    {selectedPowerGroup.extBrakingResistor.quantity}
                  </span>
                )}
                <span className="self-end text-sm color-[#494B50] w-28">
                  {t("mc12.ui.step.electrical.title.external_braking")}
                </span>
              </div>
            </div>
            {selectedPowerGroup.extBrakingResistor.quantity > 0 ? (
              <button
                onClick={() => removeBreakingResistor()}
                type="button"
                className="bg-[#DEEEF5] border-[1px] border-[#299BCD] rounded flex justify-center items-center gap-2 py-1"
              >
                <div className="rounded-full bg-[#299BCD] h-4 w-4 flex justify-center items-center self-center">
                  <LessIcon size={11} color="#DEEEF5" />
                </div>
                <span className="text-[#299BCD]">{t("mc12.ui.step.electrical.action.remove")}</span>
              </button>
            ) : (
              <button
                onClick={() => addBreakingResistor()}
                type="button"
                className="bg-[#DEEEF5] border-[1px] border-[#299BCD] rounded flex justify-center items-center gap-2 py-1"
              >
                <div className="rounded-full bg-[#299BCD] h-4 w-4 flex justify-center items-center self-center">
                  <AddIcon size={11} color="#DEEEF5" />
                </div>
                <span className="text-[#299BCD]">{t("mc12.ui.step.electrical.action.add")}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

PowerGroup.propTypes = {
  num: PropTypes.number
};

PowerGroup.defaultProps = {
  num: null
};
export default PowerGroup;
